<template>
  <template v-if="$route.name === 'class-subject-teachers'">

            <router-link
              :to="{ 
                name: 'add-class-subject-teacher',
                params: { 
                  classId: route.params.classId, 
                  subjectId: route.params.subjectId 
                } 
              }"
              class="btn btn-secondary w-100 btn-sm" 
              v-can="'class_group_teachers.create'"
            >
              <i class="fas fa-plus"></i> {{ $t('teachers.addTeacher') }}
            </router-link>
      
    <div class="card mb-5 me-0 me-md-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-2 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem"> 
            {{ $t('teachers.subjectTeachers', { subject: subject.name, class: classData.name }) }}
          </h6>
        </div>
      </div>   

      <div class="card-body px-0 pb-2 mx-2">
        <div v-if="!loading">
          <div v-if="assignments.length > 0">
            <div class="row">
              <div 
                v-for="assignment in assignments" 
                :key="assignment.id" 
                class="col-12 col-md-6 mb-4"
              >
                <TeacherAssignmentCard 
                  :assignment="assignment"             
                  @delete="confirmUnassign"
                />
              </div>
            </div>
          </div>

          <div v-else class="p-4 text-center">
            <i class="fas fa-chalkboard-teacher fa-2x mb-2 text-muted"></i>
            <p class="text-muted">{{ $t('teachers.noAssigned') }}</p>
          </div>
        </div>
        <LoadingSpinner v-else :message="$t('teachers.loading')" />
      </div>
    </div>
    
    <BaseDialog
      ref="unassignDialog"
      modal-id="unassignTeacherDialog"
      :title="$t('teachers.unassignTitle')"
      :confirm-text="$t('teachers.unassignConfirm')"
      confirm-variant="danger"
      @confirm="unassignTeacher"
    >
      <template #body>
        <p>
          {{ $t('teachers.unassignBody', { teacher: selectedAssignment?.teacher?.name, group: selectedAssignment?.group?.name }) }}
        </p>
        <p class="text-danger">{{ $t('teachers.unassignWarning') }}</p>
      </template>
    </BaseDialog>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from 'vue-toastification';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import TeacherAssignmentCard from "@/components/features/subjects/TeacherCard.vue";
import BaseDialog from '@/components/common/BaseDialog.vue';
import api from "@/api";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const toast = useToast();

const loading = ref(true);
const classData = ref({});
const subject = ref({});
const assignments = ref([]);
const selectedAssignment = ref(null);
const isUnassigning = ref(false);
const unassignDialog = ref(null);

const confirmUnassign = (assignment) => {
  selectedAssignment.value = assignment;
  unassignDialog.value.openDialog();
};

const unassignTeacher = async () => {
  if (!selectedAssignment.value) return;
  
  isUnassigning.value = true;
  try {
    await api.delete(`/class-group-teachers/${selectedAssignment.value.id}`);
    toast.success(t('teachers.unassignSuccess'));
    assignments.value = assignments.value.filter(
      a => a.id !== selectedAssignment.value.id
    );
    unassignDialog.value.closeDialog();
  } catch (error) {
    toast.error(t('teachers.unassignError'));
    console.error("Error unassigning teacher:", error);
  } finally {
    isUnassigning.value = false;
  }
};

const fetchData = async () => {
  try {
    loading.value = true;
    
    const classResponse = await api.get(`/classes/${route.params.classId}`);
    classData.value = classResponse.data.data || classResponse.data;
    
    const subjectResponse = await api.get(`/subjects/${route.params.subjectId}`);
    subject.value = subjectResponse.data.data || subjectResponse.data;
    
    const response = await api.get('/class-group-teachers', {
      params: {
        class_id: route.params.classId,
        subject_id: route.params.subjectId
      }
    });
    
    assignments.value = response.data.data || response.data;
    
  } catch (error) {
    toast.error(t('teachers.fetchError'));
    console.error("Error fetching data:", error);
    router.back();
  } finally {
    loading.value = false;
  }
};

onMounted(() => fetchData());
</script>
